import { useLayoutEffect, useRef } from "preact/hooks";
import {
  script,
  mode,
  facing,
  source,
  status,
  elapsed,
  currentTake,
  busy,
  connect,
  disconnect,
  rehearse,
  record,
  stop,
  editing,
  message,
} from "./state";
import { formatTime } from "./script";
import { Icon } from "./icons";
import { TakeCard } from "./takes";
import { CueCards } from "./cue-cards";
import { Teleprompter } from "./teleprompter";

export function Viewfinder({ active }: { active: boolean }) {
  const video = useRef<HTMLVideoElement>(null);
  const stream = source.value?.stream;
  useLayoutEffect(
    function () {
      const preview = video.current;
      if (!preview) return;
      // Set the DOM property before attaching the stream: autoplay attributes
      // alone do not reliably start a newly authorized camera on iOS.
      preview.muted = true;
      preview.srcObject = stream || null;
      let cancelled = false;
      async function startPreview(element: HTMLVideoElement) {
        try {
          await element.play();
        } catch {
          if (!cancelled) {
            message.value =
              "Camera preview could not start. Try enabling the camera again.";
            disconnect();
          }
        }
      }
      if (stream) void startPreview(preview);
      return function () {
        cancelled = true;
        preview.srcObject = null;
      };
    },
    [stream, active],
  );
  const isCards = script.value.mode === "cue-cards";
  const running = status.value === "recording" || status.value === "rehearsing";
  const isRecording = status.value === "recording";
  const isDemo = mode.value === "demo";
  const isFrontCamera = facing.value === "user";
  const mirrorPreview = isFrontCamera && !isDemo;
  let previewLabel = "PREVIEW";
  if (isRecording) previewLabel = "REC";
  else if (isDemo) previewLabel = "CAMERA-FREE TEST";

  let connectLabel = "Enable camera & mic";
  if (status.value === "opening") connectLabel = "Opening…";
  else if (isDemo) connectLabel = "Open test camera";

  let cameraLabel = isFrontCamera ? "Front camera" : "Back camera";
  if (isDemo) cameraLabel = "Synthetic video · silent";

  let recordLabel = "Record a take";
  if (isRecording) recordLabel = "Finish recording";
  else if (status.value === "saving") recordLabel = "Saving take…";
  else if (status.value === "starting") recordLabel = "Starting…";
  return (
    <section
      class={`studio ${active ? "mobile-active" : ""}`}
      aria-label="Recording studio"
    >
      <div class={`viewfinder ${stream ? "connected" : ""}`}>
        <div class="viewfinder-top">
          <span class="live-label">
            <span class={isRecording ? "recording-dot" : ""} />
            {previewLabel}
          </span>
          <span class="timecode">{formatTime(elapsed.value)}</span>
        </div>
        <div class="camera-preview">
          {/* Each stream owns a fresh video element and its native rendering layer. */}
          <video
            key={stream?.id || "disconnected"}
            ref={video}
            class={mirrorPreview ? "mirrored" : ""}
            muted
            autoPlay
            playsInline
            aria-label="Camera preview"
          />
          {(stream || running) &&
            (isCards ? (
              <CueCards running={running} />
            ) : (
              <Teleprompter running={running} active={active} />
            ))}
          {!stream && !running && (
            <div class="camera-placeholder">
              <button
                class="light-button"
                onClick={connect}
                disabled={busy.value}
              >
                {connectLabel}
                <Icon name="camera" size={17} />
              </button>
            </div>
          )}
        </div>
        <div class="viewfinder-bottom">
          <span>{cameraLabel}</span>
          <button
            class="glass-button"
            aria-label="Switch camera"
            disabled={busy.value || mode.value === "demo"}
            onClick={async function () {
              facing.value = facing.value === "user" ? "environment" : "user";
              if (source.value) await connect();
            }}
          >
            <Icon name="flip" />
          </button>
        </div>
      </div>
      <div class="transport">
        <button
          class="rehearse"
          disabled={
            !script.value.words.length ||
            editing.value ||
            (busy.value && !running)
          }
          onClick={function () {
            if (running) stop();
            else rehearse();
          }}
        >
          <Icon name={running ? "close" : "play"} size={18} />
          {running ? "Stop" : "Rehearse"}
        </button>
        <button
          class={`record-button ${isRecording ? "is-recording" : ""}`}
          disabled={
            editing.value ||
            !script.value.words.length ||
            (!source.value && !running) ||
            (busy.value && !running)
          }
          onClick={function () {
            if (running) stop();
            else record();
          }}
        >
          <span class="record-symbol" />
          {recordLabel}
        </button>
      </div>
      <div class="camera-settings">
        <label for="camera-source">Video source</label>
        <select
          id="camera-source"
          value={mode.value}
          disabled={busy.value}
          onChange={function (event) {
            disconnect();
            mode.value = event.currentTarget.value as "camera" | "demo";
          }}
        >
          <option value="camera">Camera + microphone</option>
          <option value="demo">Camera-free test</option>
        </select>
      </div>
      {currentTake.value && (
        <TakeCard take={currentTake.value} recording={currentTake.value} />
      )}
    </section>
  );
}
